/**
 * App-owned copies of the enums the UI reads off the GraphQL schema types.
 *
 * Values are byte-identical to the GraphQL schema so nothing changes at runtime while the
 * REST migration is in flight. Once a surface moves to the consumer API, remap here rather
 * than at every call site.
 */

export enum AddressMetadataType {
  Aragon = 'aragon',
  Safe = 'safe'
}

/** Safe multisig metadata payload, as returned under an {@link AddressMetadataType.Safe} entry. */
export interface SafeAddressMetadata {
  owners: string[]
  threshold: number
}

/** @internal */
export enum OracleType {
  ChainlinkOracle = 'ChainlinkOracle',
  ChainlinkOracleV2 = 'ChainlinkOracleV2',
  CustomOracle = 'CustomOracle',
  Unknown = 'Unknown'
}

export enum VaultV2TransactionType {
  Deposit = 'Deposit',
  Transfer = 'Transfer',
  Withdraw = 'Withdraw'
}

export enum VaultTimelockedFunctionName {
  RemoveMarket = 'RemoveMarket',
  SetCap = 'SetCap',
  SetGuardian = 'SetGuardian',
  SetTimelock = 'SetTimelock'
}

export enum VaultV2TimelockedFunctionName {
  Abdicate = 'Abdicate',
  AddAdapter = 'AddAdapter',
  DecreaseTimelock = 'DecreaseTimelock',
  IncreaseAbsoluteCap = 'IncreaseAbsoluteCap',
  IncreaseRelativeCap = 'IncreaseRelativeCap',
  IncreaseTimelock = 'IncreaseTimelock',
  RemoveAdapter = 'RemoveAdapter',
  SetAdapterRegistry = 'SetAdapterRegistry',
  SetForceDeallocatePenalty = 'SetForceDeallocatePenalty',
  SetIsAllocator = 'SetIsAllocator',
  SetManagementFee = 'SetManagementFee',
  SetManagementFeeRecipient = 'SetManagementFeeRecipient',
  SetPerformanceFee = 'SetPerformanceFee',
  SetPerformanceFeeRecipient = 'SetPerformanceFeeRecipient',
  SetReceiveAssetsGate = 'SetReceiveAssetsGate',
  SetReceiveSharesGate = 'SetReceiveSharesGate',
  SetSendAssetsGate = 'SetSendAssetsGate',
  SetSendSharesGate = 'SetSendSharesGate'
}

export enum VaultV2CapType {
  Adapter = 'Adapter',
  Collateral = 'Collateral',
  MarketV1 = 'MarketV1',
  Unknown = 'Unknown'
}

export enum WarningLevel {
  Red = 'RED',
  Yellow = 'YELLOW'
}

export enum VaultV2WarningLevel {
  Green = 'GREEN',
  Red = 'RED',
  Yellow = 'YELLOW'
}

/** A `{ x, y }` chart point with a nullable ordinate: the shape both GraphQL and REST emit. */
export interface NumberDataPoint {
  x: number
  y: number | null
}

export enum TimeseriesInterval {
  All = 'ALL',
  Day = 'DAY',
  FifteenMinutes = 'FIFTEEN_MINUTES',
  FiveMinutes = 'FIVE_MINUTES',
  HalfHour = 'HALF_HOUR',
  Hour = 'HOUR',
  Minute = 'MINUTE',
  Month = 'MONTH',
  Quarter = 'QUARTER',
  Week = 'WEEK',
  Year = 'YEAR'
}

export enum VaultV1LookbackPeriod {
  Inception = 'INCEPTION',
  NinetyDays = 'NINETY_DAYS',
  OneDay = 'ONE_DAY',
  OneHour = 'ONE_HOUR',
  OneYear = 'ONE_YEAR',
  SevenDays = 'SEVEN_DAYS',
  SixHours = 'SIX_HOURS',
  ThirtyDays = 'THIRTY_DAYS'
}

export enum VaultV2LookbackPeriod {
  Inception = 'INCEPTION',
  NinetyDays = 'NINETY_DAYS',
  OneDay = 'ONE_DAY',
  OneHour = 'ONE_HOUR',
  OneYear = 'ONE_YEAR',
  SevenDays = 'SEVEN_DAYS',
  SixHours = 'SIX_HOURS',
  ThirtyDays = 'THIRTY_DAYS'
}

/** Timeseries range + spacing, as accepted by the GraphQL `timeseriesOptions` input. */
export interface TimeseriesOptions {
  endTimestamp?: number | null
  interval?: TimeseriesInterval | null
  startTimestamp?: number | null
}
